import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { GoArrowLeft } from "react-icons/go";

export const AdminEditExercise = () => {
  const { staff } = useStaffAuthContext();
  const navigate = useNavigate();
  const { id } = useParams(); // Get exercise ID from URL

  // State variables for exercise fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [video_url, setVideoUrl] = useState("");
  const [type, setType] = useState(""); // Dropdown selection
  const [tags, setTags] = useState(""); // Comma-separated tags

  const [types, setTypes] = useState([]); // Store available types
  const [customType, setCustomType] = useState(""); // Custom type input
  const [isAddingCustomType, setIsAddingCustomType] = useState(false); // Toggle custom type input

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch all exercise types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await fetch(`${API_URL.GET_EXERCISE_LIBRARY_TYPES}`, {
          headers: { Authorization: `Bearer ${staff.token}` },
        });

        const data = await response.json();
        if (response.ok) {
          setTypes(data.types || []);
        } else {
          throw new Error(data.error || "Failed to fetch exercise types");
        }
      } catch (err) {
        console.error(err.message);
      }
    };

    fetchTypes();
  }, [staff.token]);

  // Fetch exercise data by ID when the component mounts
  useEffect(() => {
    const fetchExercise = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${API_URL.GET_EXERCISE_BY_ID_ADMIN}/${id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${staff.token}`,
            },
          },
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Failed to fetch exercise details");
        }

        // Populate state with fetched exercise data
        setTitle(data.title);
        setDescription(data.description);
        setVideoUrl(data.video_url);
        setType(data.type);
        setTags(data.tags.join(", ")); // Convert array to comma-separated string
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExercise();
  }, [id, staff.token]);

  // Handles form submission for updating exercise
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    const updatedExercise = {
      title,
      description,
      video_url,
      type: isAddingCustomType ? customType : type, // Use custom type if adding
      tags: tags.split(",").map((tag) => tag.trim()), // Convert to array
    };

    try {
      const response = await fetch(
        `${API_URL.POST_EDIT_EXERCISE_FROM_ADMIN}/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${staff.token}`,
          },
          body: JSON.stringify(updatedExercise),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update exercise");
      }

      setSuccessMessage("Exercise updated successfully!");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex md:h-screen">
      <StaffSideMenu />

      {/* Main Content */}
      <div className="max-md:w-full md:flex-1 bg-bgBase overflow-auto p-5 md:p-10">
        <div className="flex flex-col justify-between gap-5 mb-6">
          {/* Go Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="text-slate100 font-inter font-normal text-sm leading-tight flex items-center gap-2 hover:underline max-md:ml-12"
          >
            <GoArrowLeft color="#666666" /> Back To Library
          </button>
          <h1 className="font-inter font-medium text-dark text-3xl !leading-none">
            Edit Exercise
          </h1>
        </div>

        {/* Form Section */}
        <form
          onSubmit={handleSubmit}
          className="space-y-3 md:space-y-6 bg-white border border-tblBorder p-5 md:p-8 rounded-14"
        >
          {/* Title Input */}
          <div className="block">
            <label className="block font-inter text-slate100 text-sm font-medium">
              Exercise Title*
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter exercise title"
              className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
              required
            />
          </div>

          {/* Description Input (TextArea) */}
          <div className="block">
            <label className="block font-inter text-slate100 text-sm font-medium">
              Exercise Description*
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter exercise description"
              className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none"
              required
            />
          </div>

          {/* Video URL Input */}
          <div className="block">
            <label className="block font-inter text-slate100 text-sm font-medium">
              YouTube Video URL
            </label>
            <input
              type="text"
              value={video_url}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="Enter YouTube embed URL"
              className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
            />
          </div>

          {/* Type Selection (Dropdown or Input) */}
          <div className="block">
            <label className="block font-inter text-slate100 text-sm font-medium">
              Exercise Type*
            </label>

            {!isAddingCustomType ? (
              <select
                value={type}
                onChange={(e) => {
                  if (e.target.value === "custom") {
                    setIsAddingCustomType(true);
                    setCustomType("");
                  } else {
                    setType(e.target.value);
                  }
                }}
                className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none"
                required
              >
                <option value="">Select Type</option>
                {types.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
                <option value="custom">Add New Type</option>
              </select>
            ) : (
              <input
                type="text"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                placeholder="Enter new type"
                className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            )}
          </div>

          {/* Tags Input */}
          <div className="block">
            <label className="block font-inter text-slate100 text-sm font-medium">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="e.g. agility, endurance, core"
              className="w-full mt-2 border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 p-3 rounded-10 my-3">
              {" "}
              <p className="text-red-500 mb-4 font-inter font-medium text-sm">
                {error}
              </p>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-600/10 p-3 rounded-10 my-3">
              <p className="text-green-600 font-inter font-medium text-sm">
                {successMessage}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-between">
            <button
              type="submit"
              disabled={isLoading}
              className={`${
                isLoading ? "bg-dark/70" : " hover:hover:bg-black"
              } px-6 py-2.5 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black`}
            >
              {isLoading ? "Updating..." : "Update Exercise"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
